<script setup lang="ts">
import type {
  ExternalCourse,
  ExternalSubject,
} from '@/models/current-student-advanced-standing-form/api'
import type { LegacyClassBinding } from '@/components/current-student-advanced-standing-form/shared/types'

defineProps<{
  externalCourses: ExternalCourse[]
  coursesWithSubjects: ExternalCourse[]
  emptyExternalSubjectsMessage: (course: ExternalCourse) => string
  rowClass: (subject: ExternalSubject) => LegacyClassBinding
  isVerifiedStatus: (status: ExternalSubject['external_subject_verified']) => boolean
  canEditExternalSubject: (subject: ExternalSubject) => boolean
  canAddExternalSubject: (course: ExternalCourse) => boolean
}>()

const emit = defineEmits<{
  editSubject: [subject: ExternalSubject, course: ExternalCourse]
  addSubject: [course: ExternalCourse]
}>()
</script>

<template>
  <div id="externalSubjectsDynamicDiv">
    <div v-if="externalCourses.length === 0" class="alert alert-info">No courses provided</div>
    <div
      v-for="course in coursesWithSubjects"
      :key="course.external_course_id"
      class="ExternalSubjectContainDiv form-panel"
    >
      <fieldset>
        <legend>
          {{ course.external_course_institution?.name ?? course.external_course_institution_name }} - {{ course.external_course_name }}
        </legend>
        <div class="table-responsive">
          <table class="table table-bordered ExternalSubjectTable">
            <thead>
              <tr>
                <th>Subject Code</th>
                <th>Subject Level</th>
                <th>Year Completed</th>
                <th class="narrow"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="(course.external_subjects ?? []).length === 0">
                <td colspan="4" class="text-center">
                  {{ emptyExternalSubjectsMessage(course) }}
                </td>
              </tr>
              <tr
                v-for="subject in course.external_subjects ?? []"
                :key="subject.external_subject_id"
                :class="rowClass(subject)"
              >
                <td>{{ subject.external_subject_code }}</td>
                <td>{{ subject.external_subject_level }}</td>
                <td>{{ subject.external_subject_year }}</td>
                <td class="narrow">
                  <template v-if="isVerifiedStatus(subject.external_subject_verified)">
                    Verified
                  </template>
                  <button v-else-if="canEditExternalSubject(subject)" type="button" class="btn btn-primary btn-sm" @click="emit('editSubject', subject, course)">
                    Edit
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <button
          v-if="canAddExternalSubject(course)"
          type="button"
          class="btn btn-primary"
          @click="emit('addSubject', course)"
        >
          Add Subject
        </button>
      </fieldset>
    </div>
  </div>
</template>
